import { useState, useMemo } from "react";
import { FiX, FiCheck, FiEye } from "react-icons/fi";
import { componentList } from "../../../data/component-templates";
import { THEMES, getThemeComponentCodes } from "../../../data/component-templates/themes";

// Format component code using theme display names
// "KALE_HERO_01" → "Nova Hero 01", "HERO_01" → "Hero 01"
const THEME_PREFIX_MAP = {
  KALE: "Nova",
  CLIARO: "Clarity",
  STYLIST: "Luxe",
  ZYPGO: "Volt",
};

const formatCode = (code) => {
  const parts = code.split("_");
  if (parts.length >= 3 && THEME_PREFIX_MAP[parts[0]]) {
    const themeName = THEME_PREFIX_MAP[parts[0]];
    const section = parts.slice(1, -1).map(p => p.charAt(0) + p.slice(1).toLowerCase()).join(" ");
    return `${themeName} ${section} ${parts[parts.length - 1]}`;
  }
  // Classic: "HERO_01" → "Hero 01"
  return parts.map(p => /^\d+$/.test(p) ? p : p.charAt(0) + p.slice(1).toLowerCase()).join(" ");
};

// Map JSON category values to display names
// "common" category is split by code prefix (HEADER → Header, FOOTER → Footer)
const CATEGORY_MAP = {
  hero: "Hero",
  about: "About",
  services: "Services",
  features: "Features",
  testimonials: "Testimonials",
  pricing: "Pricing",
  cta: "CTA",
  faq: "FAQ",
  contact: "Contact",
  team: "Team",
  gallery: "Gallery",
  stats: "Stats",
  hours: "Hours",
  menu: "Menu",
  map: "Map",
  logos: "Client Logos",
  content: "Content",
  newsletter: "Newsletter",
};

const getDisplayCategory = (comp) => {
  if (comp.category === "common") {
    const prefix = comp.component_code.split("_")[0];
    return prefix.charAt(0) + prefix.slice(1).toLowerCase();
  }
  return CATEGORY_MAP[comp.category] || comp.category;
};

// Derive COMPONENTS from the centralized JSON templates
const COMPONENTS = componentList.map((comp) => ({
  code: comp.component_code,
  title: comp.title,
  description: comp.description,
  category: getDisplayCategory(comp),
}));

// Derive CATEGORIES from actual component data (preserving logical order)
const CATEGORY_ORDER = [
  "Header", "Hero", "About", "Services", "Features", "Testimonials",
  "Pricing", "CTA", "FAQ", "Contact", "Team", "Gallery",
  "Stats", "Hours", "Menu", "Map", "Logos", "Content", "Newsletter", "Footer",
];
const existingCategories = new Set(COMPONENTS.map((c) => c.category));
const CATEGORIES = ["All", ...CATEGORY_ORDER.filter((cat) => existingCategories.has(cat))];

const CATEGORY_COLORS = {
  Header: "#6366f1",
  Hero: "#ec4899",
  About: "#14b8a6",
  Services: "#f59e0b",
  Features: "#8b5cf6",
  Testimonials: "#22c55e",
  Pricing: "#3b82f6",
  CTA: "#ef4444",
  FAQ: "#06b6d4",
  Contact: "#84cc16",
  Team: "#a855f7",
  Gallery: "#f97316",
  Banner: "#be185d",
  Stats: "#64748b",
  Hours: "#0ea5e9",
  Menu: "#d946ef",
  Map: "#10b981",
  Logos: "#7c3aed",
  Content: "#0891b2",
  Newsletter: "#059669",
  Footer: "#475569",
};

// Preview image path helper
const getPreviewSrc = (code) => `/component-previews/${code}.png`;

// Helper: detect which theme a component belongs to
const getComponentThemeId = (code) => {
  if (code.startsWith('KALE_')) return 'kale';
  if (code.startsWith('CLIARO_')) return 'cliaro';
  if (code.startsWith('STYLIST_')) return 'stylist';
  if (code.startsWith('ZYPGO_')) return 'zypgo';
  return 'classic';
};

const ComponentPicker = ({ onSelect, onClose, niche, darkMode = false, currentComponentCode = null, projectTheme = 'classic' }) => {
  // Auto-select category based on current section's component_code (e.g., "HERO_02" → "Hero")
  const getInitialCategory = () => {
    if (!currentComponentCode) return "All";
    const match = COMPONENTS.find(c => c.code === currentComponentCode);
    return match ? match.category : "All";
  };

  const [selectedCategory, setSelectedCategory] = useState(getInitialCategory);
  const [selectedComponent, setSelectedComponent] = useState(() => {
    if (!currentComponentCode) return null;
    return COMPONENTS.find(c => c.code === currentComponentCode) || null;
  });
  const [purpose, setPurpose] = useState(() => {
    if (!currentComponentCode) return "";
    const match = COMPONENTS.find(c => c.code === currentComponentCode);
    return match ? match.description : "";
  });
  const [previewComponent, setPreviewComponent] = useState(null);

  // Theme colors based on darkMode
  const theme = darkMode ? {
    bg: "#1f1f1f",
    cardBg: "#2a2a2a",
    cardBorder: "#3a3a3a",
    headerBg: "#252525",
    text: "#f5f5f5",
    textMuted: "#999",
    inputBg: "#333",
    inputBorder: "#444",
    buttonBg: "#7c3aed",
    selectedBg: "#3d3d5c",
    hoverBg: "#333",
    borderColor: "#444",
  } : {
    bg: "#FFFFFF",
    cardBg: "#FFFFFF",
    cardBorder: "#E5E7EB",
    headerBg: "#FAFBFC",
    text: "#1F2937",
    textMuted: "#6B7280",
    inputBg: "#FFFFFF",
    inputBorder: "#E5E7EB",
    buttonBg: "#7C3AED",
    selectedBg: "#F5F3FF",
    hoverBg: "#F9FAFB",
    borderColor: "#E5E7EB",
  };

  // Filter components based on category
  const filteredComponents = useMemo(() => {
    return COMPONENTS.filter((comp) => {
      return selectedCategory === "All" || comp.category === selectedCategory;
    });
  }, [selectedCategory]);

  // Get recommended components based on niche
  const getRecommendedCodes = () => {
    const nicheRecommendations = {
      restaurant: ["MENU_01", "HOURS_01", "GALLERY_01", "TESTIMONIAL_01", "HERO_01"],
      dental: ["SERVICES_01", "TEAM_01", "TESTIMONIAL_01", "FAQ_01", "CONTACT_01"],
      salon: ["SERVICES_01", "PRICING_01", "GALLERY_01", "TEAM_01", "HOURS_01"],
      gym: ["SERVICES_01", "PRICING_01", "TEAM_01", "TESTIMONIAL_01", "GALLERY_01"],
      plumbing: ["SERVICES_01", "TESTIMONIAL_01", "FAQ_01", "CONTACT_01", "CTA_01"],
      roofing: ["SERVICES_01", "TESTIMONIAL_01", "FAQ_01", "CONTACT_01", "GALLERY_01"],
      medical: ["SERVICES_01", "TEAM_01", "TESTIMONIAL_01", "FAQ_01", "CONTACT_01"],
      general: ["HERO_01", "ABOUT_01", "SERVICES_01", "TESTIMONIAL_01", "CONTACT_01"],
    };
    return nicheRecommendations[niche?.toLowerCase()] || nicheRecommendations.general;
  };

  const recommendedCodes = getRecommendedCodes();

  // Theme-aware: get component codes belonging to the project's theme
  const themeComponentCodes = useMemo(() => {
    return new Set(getThemeComponentCodes(projectTheme));
  }, [projectTheme]);

  const handleSelectComponent = (comp) => {
    setSelectedComponent(comp);
    setPurpose(comp.description);
  };

  const handleConfirm = () => {
    if (selectedComponent) {
      onSelect(selectedComponent.code, purpose || selectedComponent.description);
    }
  };

  const handlePreview = (e, comp) => {
    e.stopPropagation();
    setPreviewComponent(comp);
  };

  return (
    <div
      className="component-picker-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1050,
      }}
      onClick={onClose}
    >
      <div
        className="component-picker-modal shadow-lg"
        style={{
          width: "95%",
          maxWidth: "1200px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.bg,
          border: `1px solid ${theme.borderColor}`,
          borderRadius: "14px",
          fontFamily: "'DM Sans', 'Plus Jakarta Sans', sans-serif",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="d-flex align-items-center justify-content-between p-3"
          style={{
            borderBottom: `1px solid ${theme.borderColor}`,
            backgroundColor: theme.headerBg,
            borderRadius: "12px 12px 0 0",
          }}
        >
          <h5 className="mb-0" style={{ color: theme.text, fontFamily: "'Plus Jakarta Sans', 'DM Sans', sans-serif", fontWeight: "700", letterSpacing: "-0.3px" }}>Choose a Component</h5>
          <button
            className="btn btn-sm"
            onClick={onClose}
            style={{ color: theme.textMuted, border: "none", background: "transparent" }}
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Categories */}
        <div className="p-3" style={{ borderBottom: `1px solid ${theme.borderColor}` }}>
          <div className="d-flex flex-wrap gap-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                className="btn btn-sm"
                onClick={() => setSelectedCategory(cat)}
                style={{
                  fontSize: "12px",
                  fontWeight: "500",
                  borderRadius: "8px",
                  backgroundColor: selectedCategory === cat ? theme.buttonBg : "transparent",
                  color: selectedCategory === cat ? "#fff" : theme.textMuted,
                  border: `1px solid ${selectedCategory === cat ? theme.buttonBg : theme.borderColor}`,
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Components Grid */}
        <div className="flex-grow-1 overflow-auto p-3" style={{ minHeight: "300px", backgroundColor: theme.bg, overflowX: "hidden" }}>
          {/* Recommended Section */}
          {selectedCategory === "All" && (
            <div className="mb-4">
              <h6 className="mb-2" style={{ fontSize: "12px", fontWeight: "600", color: theme.textMuted }}>
                RECOMMENDED FOR {niche?.toUpperCase() || "YOUR BUSINESS"}
              </h6>
              <div className="row g-3">
                {COMPONENTS.filter((c) => recommendedCodes.includes(c.code)).map((comp) => (
                  <div key={comp.code} className="col-lg-4 col-md-6">
                    <ComponentCard
                      component={comp}
                      isSelected={selectedComponent?.code === comp.code}
                      isRecommended={true}
                      isThemeMatch={themeComponentCodes.has(comp.code)}
                      sourceTheme={getComponentThemeId(comp.code)}
                      projectTheme={projectTheme}
                      onSelect={() => handleSelectComponent(comp)}
                      onPreview={(e) => handlePreview(e, comp)}
                      theme={theme}
                      darkMode={darkMode}
                    />
                  </div>
                ))}
              </div>
              <hr style={{ borderColor: theme.borderColor, margin: "20px 0" }} />
            </div>
          )}

          {/* All Components */}
          <div className="row g-3">
            {filteredComponents.map((comp) => (
              <div key={comp.code} className="col-lg-4 col-md-6">
                <ComponentCard
                  component={comp}
                  isSelected={selectedComponent?.code === comp.code}
                  isRecommended={recommendedCodes.includes(comp.code)}
                  isThemeMatch={themeComponentCodes.has(comp.code)}
                  sourceTheme={getComponentThemeId(comp.code)}
                  projectTheme={projectTheme}
                  onSelect={() => handleSelectComponent(comp)}
                  onPreview={(e) => handlePreview(e, comp)}
                  theme={theme}
                  darkMode={darkMode}
                />
              </div>
            ))}
          </div>

          {filteredComponents.length === 0 && (
            <div className="text-center py-5" style={{ color: theme.textMuted }}>
              <p>No components match your search.</p>
            </div>
          )}
        </div>

        {/* Footer with Purpose Input */}
        <div
          className="p-3"
          style={{
            borderTop: `1px solid ${theme.borderColor}`,
            backgroundColor: theme.headerBg,
            borderRadius: "0 0 12px 12px",
          }}
        >
          {selectedComponent ? (
            <div className="d-flex align-items-end gap-3">
              <div className="flex-grow-1">
                <label className="form-label mb-1" style={{ fontSize: "12px", color: theme.textMuted }}>
                  Section Purpose (optional)
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="e.g., Showcase our main services..."
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  style={{
                    backgroundColor: theme.inputBg,
                    borderColor: theme.inputBorder,
                    color: theme.text,
                  }}
                />
              </div>
              <button
                className="btn d-flex align-items-center gap-2"
                onClick={handleConfirm}
                style={{
                  backgroundColor: theme.buttonBg,
                  color: "#fff",
                  border: "none",
                }}
              >
                <FiCheck size={16} /> Add {formatCode(selectedComponent.code)}
              </button>
            </div>
          ) : (
            <p className="mb-0 text-center" style={{ fontSize: "13px", color: theme.textMuted }}>
              Select a component to add to your page
            </p>
          )}
        </div>
      </div>

      {/* Full-size Preview Modal */}
      {previewComponent && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            zIndex: 1060,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
          onClick={(e) => { e.stopPropagation(); setPreviewComponent(null); }}
        >
          {/* Preview header */}
          <div
            className="d-flex align-items-center justify-content-between w-100 px-4 py-3"
            style={{ maxWidth: "1200px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <h5 className="mb-0" style={{ color: "#fff" }}>{previewComponent.title}</h5>
              <small style={{ color: "#999" }}>{formatCode(previewComponent.code)} — {previewComponent.description}</small>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm d-flex align-items-center gap-1"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectComponent(previewComponent);
                  setPreviewComponent(null);
                }}
                style={{ backgroundColor: "#7c3aed", color: "#fff", border: "none" }}
              >
                <FiCheck size={14} /> Select This
              </button>
              <button
                className="btn btn-sm"
                onClick={(e) => { e.stopPropagation(); setPreviewComponent(null); }}
                style={{ color: "#999", border: "1px solid #555", background: "transparent" }}
              >
                <FiX size={16} />
              </button>
            </div>
          </div>
          {/* Preview image */}
          <div
            className="flex-grow-1 overflow-auto px-4 pb-4"
            style={{ maxWidth: "1200px", width: "100%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={getPreviewSrc(previewComponent.code)}
              alt={previewComponent.title}
              style={{
                width: "100%",
                borderRadius: "8px",
                boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Component Card Sub-component
const ComponentCard = ({ component, isSelected, isRecommended, isThemeMatch, sourceTheme, projectTheme, onSelect, onPreview, theme, darkMode }) => {
  const categoryColor = CATEGORY_COLORS[component.category] || "#6b7280";

  return (
    <div
      className="component-card"
      style={{
        cursor: "pointer",
        backgroundColor: isSelected ? theme.selectedBg : theme.cardBg,
        border: `2px solid ${isSelected ? theme.buttonBg : theme.cardBorder}`,
        borderRadius: "12px",
        transition: "all 0.15s ease",
        overflow: "hidden",
        boxShadow: isSelected ? "0 0 0 3px rgba(124, 58, 237, 0.1)" : "none",
      }}
      onClick={onSelect}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = theme.hoverBg;
          e.currentTarget.style.borderColor = darkMode ? "#555" : "#cbd5e1";
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = theme.cardBg;
          e.currentTarget.style.borderColor = theme.cardBorder;
        }
      }}
    >
      {/* Preview Image */}
      <div
        className="component-preview-wrap"
        style={{
          height: "160px",
          overflow: "hidden",
          position: "relative",
          backgroundColor: darkMode ? "#1a1a1a" : "#F9FAFB",
          borderBottom: `1px solid ${theme.cardBorder}`,
        }}
      >
        <img
          src={getPreviewSrc(component.code)}
          alt={component.title}
          loading="lazy"
          style={{
            width: "100%",
            height: "auto",
            display: "block",
          }}
        />
        {/* Hover overlay with preview button */}
        <div
          className="preview-overlay"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            opacity: 0,
            transition: "opacity 0.2s ease",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = 0; }}
        >
          <button
            className="btn btn-sm d-flex align-items-center gap-1"
            onClick={onPreview}
            style={{
              backgroundColor: "rgba(255,255,255,0.95)",
              color: "#1f2937",
              border: "none",
              fontSize: "12px",
              fontWeight: "600",
              borderRadius: "6px",
              padding: "6px 12px",
            }}
          >
            <FiEye size={14} /> Preview
          </button>
        </div>
        {/* Selection overlay */}
        {isSelected && (
          <div
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              width: 24,
              height: 24,
              borderRadius: "50%",
              backgroundColor: theme.buttonBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FiCheck size={14} color="#fff" />
          </div>
        )}
        {/* Recommended badge */}
        {isRecommended && (
          <span
            className="badge"
            style={{
              position: "absolute",
              top: 8,
              left: 8,
              fontSize: "9px",
              backgroundColor: darkMode ? "#166534" : "#dcfce7",
              color: darkMode ? "#dcfce7" : "#166534",
            }}
          >
            Recommended
          </span>
        )}
        {/* Theme source badge — show when component is from a different theme */}
        {sourceTheme !== 'classic' && sourceTheme !== projectTheme && (
          <span
            className="badge"
            style={{
              position: "absolute",
              bottom: 8,
              left: 8,
              fontSize: "9px",
              backgroundColor: darkMode ? "#1e293b" : "#e2e8f0",
              color: darkMode ? "#94a3b8" : "#475569",
            }}
          >
            {THEMES[sourceTheme]?.name || sourceTheme}
          </span>
        )}
        {/* Theme match badge */}
        {isThemeMatch && sourceTheme !== 'classic' && (
          <span
            className="badge"
            style={{
              position: "absolute",
              bottom: 8,
              left: 8,
              fontSize: "9px",
              backgroundColor: THEMES[projectTheme]?.defaultStyleGuide?.colors?.primary || "#7c3aed",
              color: "#fff",
            }}
          >
            {THEMES[projectTheme]?.name} Theme
          </span>
        )}
      </div>

      {/* Title bar */}
      <div className="d-flex align-items-center justify-content-between px-2 py-2">
        <span style={{ fontSize: "12px", fontWeight: "600", color: theme.text }}>{component.title}</span>
        <span
          style={{
            fontSize: "10px",
            fontWeight: "500",
            color: categoryColor,
            backgroundColor: darkMode ? `${categoryColor}25` : `${categoryColor}15`,
            padding: "2px 6px",
            borderRadius: "4px",
          }}
        >
          {formatCode(component.code)}
        </span>
      </div>
    </div>
  );
};

export default ComponentPicker;
